import { useQuery } from '@tanstack/react-query';
import { useApiClient } from './useApiClient';

export interface ReadDataPoint {
  /** Meter-local wall-clock time of the reading (timezone-naive, like flow). */
  readingTime: string;
  /** Monotonic lifetime total, always in gallons. */
  cumulativeGallons: number | null;
  /** What the physical meter LCD shows (rolls over); in `lcdReadUnit`. */
  lcdRead: number | null;
  /** Unit of `lcdRead`: 'G' (gallons), 'F' (cubic feet), 'M', or null. */
  lcdReadUnit: string | null;
  /**
   * True for readings inside the requested [start, end] window. The series also
   * includes one "bracket" reading just outside each end (inRange = false) so
   * the chart line can extend to the plot edges; range deltas ignore these.
   */
  inRange: boolean;
}

export interface ReadsSummary {
  latestReadingTime: string;
  /** Most recent LCD read value, in `lcdReadUnit`. */
  lcdRead: number | null;
  lcdReadUnit: string | null;
  /** Trailing-window consumption in gallons; null when no reading that far back. */
  last7Days: number | null;
  last30Days: number | null;
  last90Days: number | null;
}

interface MeterReadsResponse {
  reads: ReadDataPoint[];
  summary: ReadsSummary | null;
}

export function useMeterReads(
  sensorId: string | undefined,
  dateRange?: { from: Date; to: Date },
) {
  const apiClient = useApiClient();

  return useQuery({
    queryKey: ['meter-reads', sensorId, dateRange?.from, dateRange?.to],
    queryFn: () => {
      const params = dateRange
        ? `?start=${dateRange.from.toISOString()}&end=${dateRange.to.toISOString()}`
        : '';
      return apiClient.get<MeterReadsResponse>(`meters/${sensorId}/reads${params}`);
    },
    enabled: !!sensorId,
  });
}
